import { useMemo } from 'react';

import { TSparseReplica } from '@modules/replicas';
import { TERMINOLOGY } from '@shared/i18n';
import SidebarTree, { buildVersionGroupedSections, TSidebarTreeSection } from './SidebarTree';

type TProps = {
  replicaListings?: TSparseReplica[];
  versions: { id: string }[];
};

const ReplicaListingsTree = ({ replicaListings, versions }: TProps) => {
  const sections = useMemo<TSidebarTreeSection[]>(() => {
    if (!replicaListings || replicaListings.length === 0) return [];

    const autoReplicas: TSparseReplica[] = [];
    const draftReplicas: TSparseReplica[] = [];
    const byVersion: Record<string, TSparseReplica[]> = {};

    for (const r of replicaListings) {
      if (!r.versionId) {
        autoReplicas.push(r);
      } else if (r.versionId === 'draft') {
        draftReplicas.push(r);
      } else {
        (byVersion[r.versionId] ??= []).push(r);
      }
    }

    const result: TSidebarTreeSection[] = [];
    const toItem = (r: TSparseReplica) => ({
      id: r._id,
      label: r.name,
      href: `/synced/${r._id}`,
      target: '_blank' as const,
      rel: 'noopener noreferrer',
    });

    if (autoReplicas.length > 0) {
      result.push({ id: 'auto', label: 'Auto', items: autoReplicas.map(toItem) });
    }

    if (draftReplicas.length > 0) {
      result.push({ id: 'draft', label: 'Draft', items: draftReplicas.map(toItem) });
    }

    result.push(...buildVersionGroupedSections(versions, byVersion, (r) => `/synced/${r._id}`));

    return result;
  }, [replicaListings, versions]);

  const treeProps = useMemo(
    () => (sections.length > 0 ? { defaultOpenItems: [sections[0].id] } : undefined),
    [sections]
  );

  return <SidebarTree ariaLabel={TERMINOLOGY.replica.upperPlural} sections={sections} treeProps={treeProps} />;
};

export default ReplicaListingsTree;
